import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useDeferredValue,
} from "react";
import { debounce } from "lodash";
import SingleDatePicker from "./SingleDatePicker";
import "../components/Calendar.css";
import { getRowStyle } from "../utils/utils";

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i} className="py-3 px-4 border-b border-gray-200">
        <div className="h-4 bg-gray-200 rounded"></div>
      </td>
    ))}
  </tr>
);

const UnassignedWOTable = ({
  workOrders,
  onDragStart,
  isReadOnly,
  selectedDate,
  isLoading,
  error,
  categories,
  canAssign = true,
}) => {
  const [filterInput, setFilterInput] = useState({
    woNumber: "",
    projectNumber: "",
    dateRange: { start: null, end: null },
    status: "",
    allergens: [],
    productCategories: [],
  });
  const filters = useDeferredValue(filterInput);

  const [sortConfig, setSortConfig] = useState({
    key: "woNumber",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isProductCategoryDropdownOpen, setIsProductCategoryDropdownOpen] =
    useState(false);
  const [isAllergenDropdownOpen, setIsAllergenDropdownOpen] = useState(false);
  const pageSize = 20;
  const dropdownRef = useRef(null);
  const allergenDropdownRef = useRef(null);

  const allergenTypes = [
    "Mustard",
    "Sesame",
    "Celery",
    "Mustard, Sesame",
    "Mustard, Celery",
  ];

  const handleProductCategoryChange = (category) => {
    setFilterInput((prev) => {
      const productCategories = prev.productCategories.includes(category)
        ? prev.productCategories.filter((c) => c !== category)
        : [...prev.productCategories, category];
      return { ...prev, productCategories };
    });
    setCurrentPage(1);
  };

  const handleAllergenChange = (allergen) => {
    setFilterInput((prev) => {
      const allergens = prev.allergens.includes(allergen)
        ? prev.allergens.filter((a) => a !== allergen)
        : [...prev.allergens, allergen];
      return { ...prev, allergens };
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterInput({
      woNumber: "",
      projectNumber: "",
      dateRange: { start: null, end: null },
      status: "",
      allergens: [],
      productCategories: [],
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.woNumber,
    filters.projectNumber,
    filters.status,
    filters.dateRange.start,
    filters.dateRange.end,
    JSON.stringify(filters.allergens),
    JSON.stringify(filters.productCategories),
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductCategoryDropdownOpen(false);
      }
      if (
        allergenDropdownRef.current &&
        !allergenDropdownRef.current.contains(event.target)
      ) {
        setIsAllergenDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSort = (key) => {
    if (key === "scheduledTime") return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const filteredWorkOrders = useMemo(() => {
    if (!workOrders || !Array.isArray(workOrders)) return [];

    const filtered = workOrders.filter((wo) => {
      const matchesWoNumber = (wo.woNumber || "")
        .toLowerCase()
        .includes(filters.woNumber.toLowerCase());

      const matchesProjectNumber = (wo.projectNumber || "")
        .toLowerCase()
        .includes(filters.projectNumber.toLowerCase());

      const matchesDateRange =
  !filters.dateRange.start ||
  !filters.dateRange.end ||
  (wo.createdAt && (() => {
    let woDateStr = wo.createdAt;
    if (woDateStr instanceof Date) {
      woDateStr = woDateStr.toISOString().split('T')[0];
    }

    if (!woDateStr || typeof woDateStr !== 'string') return false;

    // Compare only the date part (YYYY-MM-DD)
    const woDatePart = woDateStr.split('T')[0];

    const startStr = new Date(filters.dateRange.start)
      .toISOString()
      .split('T')[0];

    const endStr = new Date(filters.dateRange.end)
      .toISOString()
      .split('T')[0];

    return woDatePart >= startStr && woDatePart <= endStr;
  })());

      const matchesStatus = !filters.status || wo.status === filters.status;

      const matchesAllergen =
        filters.allergens.length === 0 ||
        filters.allergens.includes(wo.allergen || "");

      const matchesProductCategory =
        filters.productCategories.length === 0 ||
        filters.productCategories.includes(wo.label || ""); 

      return (
        matchesWoNumber &&
        matchesProjectNumber &&
        matchesDateRange &&
        matchesStatus &&
        matchesAllergen &&
        matchesProductCategory
      );
    });

    return filtered;
  }, [workOrders, filters]);

  const sortedWorkOrders = useMemo(() => {
    const sorted = [...filteredWorkOrders];
    sorted.sort((a, b) => {
      const key = sortConfig.key;
      if (key === "scheduledTime") {
        const timeA = a[key] || "00:00";
        const timeB = b[key] || "00:00";
        return sortConfig.direction === "asc"
          ? timeA.localeCompare(timeB)
          : timeB.localeCompare(timeA);
      } else if (key === "plannedQty" || key === "actualQty") {
        const valueA = a[key] || 0;
        const valueB = b[key] || 0;
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      } else if (key === "date") {
        const dateA = new Date(a[key] || "1970-01-01");
        const dateB = new Date(b[key] || "1970-01-01");
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }
      const aValue = a[key] || "";
      const bValue = b[key] || "";
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
    return sorted;
  }, [filteredWorkOrders, sortConfig]);

  const totalPages = Math.ceil(sortedWorkOrders.length / pageSize) || 1;
  const paginatedWorkOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedWorkOrders.slice(startIndex, endIndex);
  }, [sortedWorkOrders, currentPage, pageSize]);

  const paginate = useCallback(
    (pageNumber) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
      }
    },
    [totalPages],
  );

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg border border-gray-200">
      <h3 className="text-lg sm:text-xl font-semibold mb-4 pb-3 border-b border-gray-200 text-gray-800">
        Unassigned Work Orders
      </h3>

      {/* Filters */}
      <div className="mb-4 sm:mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <div>
          <label
            htmlFor="woNumber"
            className="block text-xs sm:text-sm font-medium text-gray-900 mb-1"
          >
            WO Number
          </label>
          <input
            id="woNumber"
            type="text"
            value={filterInput.woNumber}
            onChange={(e) =>
              setFilterInput((prev) => ({ ...prev, woNumber: e.target.value }))
            }
            placeholder="Enter WO Number"
            aria-label="Filter by Work Order Number"
            className="w-full py-1 sm:py-2 px-2 sm:px-3 rounded border border-gray-300 bg-white text-xs sm:text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <label
            htmlFor="projectNumber"
            className="block text-xs sm:text-sm font-medium text-gray-900 mb-1"
          >
            Project Number
          </label>
          <input
            id="projectNumber"
            type="text"
            value={filterInput.projectNumber}
            onChange={(e) =>
              setFilterInput((prev) => ({
                ...prev,
                projectNumber: e.target.value,
              }))
            }
            placeholder="Enter Project Number"
            aria-label="Filter by Project Number"
            className="w-full py-1 sm:py-2 px-2 sm:px-3 rounded border border-gray-300 bg-white text-xs sm:text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <label
            htmlFor="status"
            className="block text-xs sm:text-sm font-medium text-gray-900 mb-1"
          >
            Status
          </label>
          <select
            id="status"
            value={filterInput.status}
            onChange={(e) =>
              setFilterInput((prev) => ({ ...prev, status: e.target.value }))
            }
            aria-label="Filter by Status"
            className="w-full py-1 sm:py-2 px-2 sm:px-3 rounded border border-gray-300 bg-white text-xs sm:text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
          >
            <option value="">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="planned">Planned</option>
            <option value="in progress">In Progress</option>
          </select>
        </div>
        <div className="relative">
          <label
            htmlFor="allergens"
            className="block text-xs sm:text-sm font-medium text-gray-900 mb-1"
          >
            Allergens
          </label>
          <button
            id="allergens"
            type="button"
            onClick={() => setIsAllergenDropdownOpen(!isAllergenDropdownOpen)}
            className="w-full py-1 sm:py-2 px-2 sm:px-3 rounded border border-gray-300 bg-white text-xs sm:text-sm text-gray-900 text-left focus:ring-2 focus:ring-gray-500 focus:outline-none"
            aria-label="Toggle allergen filter dropdown"
          >
            {filterInput.allergens.length === 0
              ? "All Allergens"
              : filterInput.allergens.join(", ")}
          </button>
          {isAllergenDropdownOpen && (
            <div
              ref={allergenDropdownRef}
              className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
            >
              <label className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterInput.allergens.length === 0}
                  onChange={() =>
                    setFilterInput((prev) => ({ ...prev, allergens: [] }))
                  }
                  className="mr-2"
                  aria-label="Select all allergens"
                />
                All
              </label>
              {allergenTypes.map((allergen) => (
                <label
                  key={allergen}
                  className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filterInput.allergens.includes(allergen)}
                    onChange={() => handleAllergenChange(allergen)}
                    className="mr-2"
                    aria-label={`Filter by ${allergen} allergen`}
                  />
                  {allergen}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="relative">
          <label
            htmlFor="productCategories"
            className="block text-xs sm:text-sm font-medium text-gray-900 mb-1"
          >
            Product Category
          </label>
          <button
            id="productCategories"
            type="button"
            onClick={() =>
              setIsProductCategoryDropdownOpen(!isProductCategoryDropdownOpen)
            }
            className="w-full py-1 sm:py-2 px-2 sm:px-3 rounded border border-gray-300 bg-white text-xs sm:text-sm text-gray-900 text-left focus:ring-2 focus:ring-gray-500 focus:outline-none"
            aria-label="Toggle product category filter dropdown"
          >
            {filterInput.productCategories.length === 0
              ? "All Categories"
              : filterInput.productCategories.join(", ")}
          </button>
          {isProductCategoryDropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
            >
              <label className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.productCategories.length === 0}
                  onChange={() =>
                    setFilterInput((prev) => ({
                      ...prev,
                      productCategories: [],
                    }))
                  }
                  className="mr-2"
                  aria-label="Select all product categories"
                />
                All
              </label>
              {categories.map((category) => (
                <label
                  key={category}
                  className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filters.productCategories.includes(category)}
                    onChange={() => handleProductCategoryChange(category)}
                    className="mr-2"
                    aria-label={`Filter by ${category} product category`}
                  />
                  {category}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-end mb-4">
        <div className="flex flex-col flex-1">
          <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1">
            Date Range
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <SingleDatePicker
              value={filters.dateRange.start}
              onChange={(date) =>
                setFilterInput((prev) => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, start: date },
                }))
              }
              minDate={new Date(2025, 0, 1)}
              placeholderText="Start Date"
              className="text-gray-900 flex-1 text-xs sm:text-sm"
            />
            <SingleDatePicker
              value={filters.dateRange.end}
              onChange={(date) =>
                setFilterInput((prev) => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, end: date },
                }))
              }
              minDate={filters.dateRange.start || new Date(2025, 0, 1)}
              placeholderText="End Date"
              className="text-gray-900 flex-1 text-xs sm:text-sm"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 items-center">
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 py-2 px-4 rounded-lg text-white bg-[#cf350d] hover:bg-red-700 text-sm font-medium transition"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table for larger screens */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full border-collapse mt-4">
          <thead>
            <tr>
              {[
                "WO Number",
                "Project #",
                "SKU",
                "Title",
                "Label",
                "Allergen",
                "Net Weight",
                "Planned Qty",
                "Actual Qty",
              ].map((header) => (
                <th
                  key={header}
                  onClick={() =>
                    handleSort(
                      header
                        .toLowerCase()
                        .replace(" #", "Number")
                        .replace(" ", ""),
                    )
                  }
                  className={`py-2 sm:py-3 px-2 sm:px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300 cursor-pointer hover:bg-gray-300 ${
                    header === "WO Number" ? "whitespace-nowrap" : ""
                  }`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleSort(
                        header
                          .toLowerCase()
                          .replace(" #", "Number")
                          .replace(" ", ""),
                      );
                    }
                  }}
                  aria-label={`Sort by ${header}`}
                >
                  {header}{" "}
                  {sortConfig.key ===
                    header
                      .toLowerCase()
                      .replace(" #", "Number")
                      .replace(" ", "") && (
                    <span>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <>
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonRow key={i} />
                ))}
              </>
            ) : error ? (
              <tr>
                <td
                  colSpan="9"
                  className="text-center py-8 sm:py-10 text-red-600 text-sm sm:text-base"
                >
                  {error}
                </td>
              </tr>
            ) : paginatedWorkOrders.length > 0 ? (
              paginatedWorkOrders.map((wo) => {
                const isDraggable = canAssign;
                return (
                  <tr
                    key={wo._id}
                    draggable={isDraggable}
                    onDragStart={
                      isDraggable ? (e) => onDragStart(e, wo) : undefined
                    }
                    onDrag={
                      isDraggable
                        ? (e) => {
                            e.currentTarget.style.opacity = "0.7";
                            e.currentTarget.style.border = "2px dashed";
                          }
                        : undefined
                    }
                    onDragEnd={
                      isDraggable
                        ? (e) => {
                            e.currentTarget.style.opacity = "1";
                            e.currentTarget.style.border = "none";
                          }
                        : undefined
                    }
                    tabIndex={isDraggable ? 0 : -1}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isDraggable) {
                        onDragStart(e, wo);
                      }
                    }}
                    className={`transition-all duration-200 ${
                      isDraggable
                        ? "cursor-grab hover:bg-opacity-50"
                        : "cursor-not-allowed opacity-60"
                    } focus:ring-2 focus:ring-gray-500 focus:outline-none`}
                    style={getRowStyle(
                      wo.allergen,
                      wo.status,
                      null,
                      wo.allergens,
                    )}
                    aria-label={`Work Order ${wo.woNumber}, Status: ${
                      wo.status
                    }, Allergen: ${wo.allergen || "None"}, ${
                      isDraggable ? "Draggable" : "Not Draggable"
                    }`}
                    aria-grabbed={isDraggable}
                  >
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 whitespace-nowrap">
                      {wo.woNumber}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.projectNumber}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.sku || "N/A"}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.title}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.labelCategory || "None"}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.allergen || "None"}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.netWeight || "N/A"}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.plannedQty || 0}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                      {wo.actualQty || 0}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="9"
                  className="text-center py-8 sm:py-10 text-gray-600 text-sm sm:text-base"
                >
                  No unassigned work orders match the filters.{" "}
                  <button
                    onClick={clearFilters}
                    className="text-gray-900 hover:text-gray-700 underline"
                    aria-label="Clear filters to view all work orders"
                  >
                    Clear filters
                  </button>{" "}
                  to view all work orders.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="block sm:hidden mt-4 space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-200 animate-pulse"
              >
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Array.from({ length: 10 }).map((_, j) => (
                    <div key={j} className="space-y-1">
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                      <div className="h-4 bg-gray-300 rounded w-24"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div
            className="text-center py-8 text-red-600 text-sm"
            aria-label="Error loading unassigned work orders"
          >
            {error}
          </div>
        ) : paginatedWorkOrders.length > 0 ? (
          paginatedWorkOrders.map((wo) => {
            const isDraggable = canAssign;
            return (
              <div
                key={wo._id}
                draggable={isDraggable}
                onDragStart={
                  isDraggable ? (e) => onDragStart(e, wo) : undefined
                }
                onDrag={
                  isDraggable
                    ? (e) => {
                        e.currentTarget.style.opacity = "0.7";
                        e.currentTarget.style.border = "2px dashed";
                      }
                    : undefined
                }
                onDragEnd={
                  isDraggable
                    ? (e) => {
                        e.currentTarget.style.opacity = "1";
                        e.currentTarget.style.border = "none";
                      }
                    : undefined
                }
                tabIndex={isDraggable ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isDraggable) {
                    onDragStart(e, wo);
                  }
                }}
                className={`bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 ${
                  isDraggable
                    ? "cursor-grab hover:bg-opacity-50"
                    : "cursor-not-allowed opacity-60"
                }`}
                style={getRowStyle(wo.allergen, wo.status, null, wo.allergens)}
                aria-label={`Work Order ${wo.woNumber}, Status: ${
                  wo.status
                }, Allergen: ${wo.allergen || "None"}, ${
                  isDraggable ? "Draggable" : "Not Draggable"
                }`}
                aria-grabbed={isDraggable}
              >
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="font-semibold text-gray-700">WO Number</div>
                  <div>{wo.woNumber}</div>
                  <div className="font-semibold text-gray-700">Project #</div>
                  <div>{wo.projectNumber}</div>
                  <div className="font-semibold text-gray-700">SKU</div>
                  <div>{wo.sku || "N/A"}</div>
                  <div className="font-semibold text-gray-700">Title</div>
                  <div>{wo.title}</div>
                  <div className="font-semibold text-gray-700">Label</div>
                  <div>{wo.labelCategory || "None"}</div>
                  <div className="font-semibold text-gray-700">Allergen</div>
                  <div>{wo.allergen || "None"}</div>
                  <div className="font-semibold text-gray-700">Net Weight</div>
                  <div>{wo.netWeight || "N/A"}</div>
                  <div className="font-semibold text-gray-700">Planned Qty</div>
                  <div>{wo.plannedQty || 0}</div>
                  <div className="font-semibold text-gray-700">Actual Qty</div>
                  <div>{wo.actualQty || 0}</div>
                </div>
              </div>
            );
          })
        ) : (
          <div
            className="text-center py-8 text-gray-600 text-sm"
            aria-label="No unassigned work orders"
          >
            No unassigned work orders match the filters.{" "}
            <button
              onClick={clearFilters}
              className="text-gray-900 hover:text-gray-700 underline"
              aria-label="Clear filters to view all work orders"
            >
              Clear filters
            </button>{" "}
            to view all work orders.
          </div>
        )}
      </div>

      {/* Card layout for mobile */}
      <div className="block sm:hidden mt-4 space-y-3">
        {isLoading ? (
          <div
            className="text-center py-8 text-gray-600 text-sm"
            aria-label="Loading unassigned work orders"
          >
            Loading unassigned work orders...
          </div>
        ) : error ? (
          <div
            className="text-center py-8 text-red-600 text-sm"
            aria-label="Error loading unassigned work orders"
          >
            {error}
          </div>
        ) : paginatedWorkOrders.length > 0 ? (
          paginatedWorkOrders.map((wo) => {
            const isDraggable = true;
            return (
              <div
                key={wo._id}
                draggable={isDraggable}
                onDragStart={
                  isDraggable ? (e) => onDragStart(e, wo) : undefined
                }
                onDrag={
                  isDraggable
                    ? (e) => {
                        e.currentTarget.style.opacity = "0.7";
                        e.currentTarget.style.border = "2px dashed";
                      }
                    : undefined
                }
                onDragEnd={
                  isDraggable
                    ? (e) => {
                        e.currentTarget.style.opacity = "1";
                        e.currentTarget.style.border = "none";
                      }
                    : undefined
                }
                tabIndex={isDraggable ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isDraggable) {
                    onDragStart(e, wo);
                  }
                }}
                className={`bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 ${
                  isDraggable
                    ? "cursor-grab hover:bg-opacity-50"
                    : "cursor-not-allowed opacity-60"
                }`}
                style={getRowStyle(wo.allergen, wo.status, null, wo.allergens)}
                aria-label={`Work Order ${wo.woNumber}, Status: ${
                  wo.status
                }, Allergen: ${wo.allergen || "None"}, ${
                  isDraggable ? "Draggable" : "Not Draggable"
                }`}
                aria-grabbed={isDraggable}
              >
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="font-semibold text-gray-700">
                    Scheduled Hour
                  </div>
                  <div>
                    {wo.scheduledTime
                      ? new Date(
                          `1970-01-01T${wo.scheduledTime}`,
                        ).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "Not Set"}
                    {wo.duration && ` (${wo.duration} min)`}
                  </div>
                  <div className="font-semibold text-gray-700">WO Number</div>
                  <div>{wo.woNumber}</div>
                  <div className="font-semibold text-gray-700">Project #</div>
                  <div>{wo.projectNumber}</div>
                  <div className="font-semibold text-gray-700">SKU</div>
                  <div>{wo.sku || "N/A"}</div>
                  <div className="font-semibold text-gray-700">Title</div>
                  <div>{wo.title}</div>
                  <div className="font-semibold text-gray-700">Label</div>
                  <div>{wo.labelCategory || "None"}</div>
                  <div className="font-semibold text-gray-700">Allergen</div>
                  <div>{wo.allergen || "None"}</div>
                  <div className="font-semibold text-gray-700">Net Weight</div>
                  <div>{wo.netWeight || "N/A"}</div>
                  <div className="font-semibold text-gray-700">Planned Qty</div>
                  <div>{wo.plannedQty || 0}</div>
                  <div className="font-semibold text-gray-700">Actual Qty</div>
                  <div>{wo.actualQty || 0}</div>
                </div>
              </div>
            );
          })
        ) : (
          <div
            className="text-center py-8 text-gray-600 text-sm"
            aria-label="No unassigned work orders"
          >
            No unassigned work orders match the filters.{" "}
            <button
              onClick={clearFilters}
              className="text-gray-900 hover:text-gray-700 underline"
              aria-label="Clear filters to view all work orders"
            >
              Clear filters
            </button>{" "}
            to view all work orders.
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-full sm:w-auto px-3 sm:px-4 py-1 sm:py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 disabled:opacity-50 text-xs sm:text-base"
            aria-label="Previous page"
          >
            Previous
          </button>
          <span className="text-gray-600 text-xs sm:text-base">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-full sm:w-auto px-3 sm:px-4 py-1 sm:py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 disabled:opacity-50 text-xs sm:text-base"
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default UnassignedWOTable;
